'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

type JobImageSlideProps = {
  imageUrls: string[];
  jobTitle?: string;
};

const JobImageSlide: React.FC<JobImageSlideProps> = ({
  imageUrls,
  jobTitle,
}) => {
  const mainSplideRef = useRef<import('@splidejs/splide').default | null>(null);
  const carouselRef = useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Splideの初期化（画像が複数ある場合のみ）
  useEffect(() => {
    if (imageUrls.length <= 1) return;

    let link: HTMLLinkElement | null = null;
    let mounted = true;

    // CSSとJSを並行して読み込み
    const loadSplide = async () => {
      try {
        // CSS読み込み
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href =
          'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css';
        document.head.appendChild(link);

        // JS読み込み
        const { default: SplideClass } = await import('@splidejs/splide');

        // コンポーネントがアンマウントされていないかチェック
        if (!mounted) return;

        // DOM要素の存在確認
        const carouselElement = carouselRef.current;
        if (!carouselElement) return;

        // 既存のSplideインスタンスがあれば削除
        if (mainSplideRef.current) {
          mainSplideRef.current.destroy();
          mainSplideRef.current = null;
        }

        const main = new SplideClass(carouselElement, {
          type: 'loop',
          perPage: 1,
          pagination: false,
          arrows: true,
          lazyLoad: 'nearby',
          preloadPages: 1,
        });

        main.on('moved', (newIndex) => {
          if (mounted) {
            setActiveIndex(newIndex);
          }
        });

        main.mount();
        mainSplideRef.current = main;
      } catch (error) {
        console.error('Splide読み込みエラー:', error);
      }
    };

    loadSplide();

    // クリーンアップ関数を同期的に返す
    return () => {
      mounted = false;
      if (mainSplideRef.current) {
        mainSplideRef.current.destroy();
        mainSplideRef.current = null;
      }
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [imageUrls]); // imageUrls.lengthではなくimageUrls自体を依存配列に

  if (imageUrls.length === 0) return null;

  return (
    <>
      {imageUrls.length > 1 ? (
        <>
          <section
            ref={carouselRef}
            id="main-carousel"
            className="splide"
            aria-label="お気に入りの写真"
          >
            <div className="splide__track">
              <ul className="splide__list">
                {imageUrls.map((url, index) => (
                  <li className="splide__slide" key={index}>
                    <Image
                      src={url}
                      alt={jobTitle || '求人詳細の画像'}
                      width={300}
                      height={200}
                      className="p-job-detail__image"
                      priority={index === 0}
                      sizes="(max-width: 768px) 100vw, 300px"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      unoptimized
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <ul id="thumbnails" className="splide__thumbnails">
            {imageUrls.map((url, index) => (
              <li
                key={index}
                className={`splide__thumbnail ${
                  index === activeIndex ? 'is-active' : ''
                }`}
                onClick={() => {
                  if (mainSplideRef.current) {
                    mainSplideRef.current.go(index);
                    setActiveIndex(index);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <Image
                  src={url}
                  alt={jobTitle || '求人詳細の画像'}
                  width={70}
                  height={50}
                  className="splide__thumbnail-img"
                  loading="lazy"
                  sizes="70px"
                  unoptimized
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <Image
          src={imageUrls[0]}
          alt={jobTitle || '求人詳細の画像'}
          width={300}
          height={200}
          className="p-job-detail__image"
          priority
          sizes="(max-width: 768px) 100vw, 300px"
          unoptimized
        />
      )}
    </>
  );
};

export default JobImageSlide;
